import { t } from 'i18next';
import { action, makeAutoObservable, runInAction } from 'mobx';
import {
  LatestSolverJobForVersionDocument,
  LatestSolverJobForVersionQuery,
  LatestSolverJobForVersionQueryVariables,
  SolverEngine,
  SolverJobDocument,
  SolverJobQuery,
  SolverJobQueryVariables,
} from '../types/planung-graphql-client-defs';
import {
  isMathplanJobFailureTerminal,
  isMathplanJobSuccessTerminal,
  isMathplanJobTerminalForUi,
} from '../utils/mathplanJobStatus';
import {
  cancelTimetableVersionGeneration,
  finishTimetableVersionGeneration,
  solveTimetableVersionWithParams,
} from '../utils/timetable/timetableVersion';
import type { GenericWarning } from '../utils/timetable/types';
import { urqlClient } from '../utils/urqlClient';
import type { UserConfigStore } from './userConfig/UserConfigStore';

/**
 * Alte, geraetegebundene Ablage. Engine und Geschwindigkeit liegen jetzt pro
 * Person in der UserConfig (`scope: 'solver'`); die localStorage-Werte werden
 * beim ersten Laden einmalig uebernommen und danach geloescht.
 */
const ENGINE_STORAGE_KEY = 'tk-planung.solverEngine';

function readLegacyEngine(): SolverEngine | null {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(ENGINE_STORAGE_KEY);
  window.localStorage.removeItem(ENGINE_STORAGE_KEY);

  // Ein unbekannter Wert — etwa ein ASC/Untis aus dem entfernten Offline-Solver —
  // zaehlt wie keiner; dann greift der Default aus der UserConfig.
  return raw === SolverEngine.Mathplan || raw === SolverEngine.Timefold ? raw : null;
}

/** Timefold solve-speed presets; the backend maps the key to a spentLimit in seconds. */
export type SolverSpeed = 'fast' | 'balanced' | 'thorough';

/**
 * What a solver run should do (mirrors the backend SOLVE_TASKS discriminator, minus ANALYZE which
 * has its own non-solving path). The backend turns this into the per-card pin/warm-start/room
 * combination — see solver/types/export.ts.
 * - GENERATE     — (re)place cards; locked cards stay fixed; the classic wizard flow.
 * - OPTIMIZE     — improve the current placement (movable warm-start) for the selected classes;
 *                  other classes stay pinned unless `allowMoveOtherClasses` is set.
 * - ASSIGN_ROOMS — distribute rooms onto the existing, time-fixed plan (cards are not moved).
 */
export type SolverTask = 'GENERATE' | 'OPTIMIZE' | 'ASSIGN_ROOMS';

const SPEED_STORAGE_KEY = 'tk-planung.solverSpeed';

function readLegacySpeed(): SolverSpeed | null {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(SPEED_STORAGE_KEY);
  window.localStorage.removeItem(SPEED_STORAGE_KEY);

  return raw === 'fast' || raw === 'balanced' || raw === 'thorough' ? raw : null;
}

/** One solver-proposed card placement, parsed from the previewCards JSON envelope. */
export type PreviewPlacement = {
  weekday: string | null;
  startTimeGridEntryUuid: string | null;
  endTimeGridEntryUuid: string | null;
  roomUuids: string[];
};

export type SolverJobSnapshot = {
  gap: number;
  jobId: string;
  solutionFound: boolean;
  status: string;
  engine: SolverEngine;
  /** JSON string from backend (Timefold status + optional breakdown). */
  scoreDetails?: string | null;
  /** JSON envelope of the running solver's intermediate solution (live preview). */
  previewCards?: string | null;
  /** Server-side JSON of the generator options chosen for this job (for the run banner chips). */
  jobOptions?: string | null;
  /**
   * ISO timestamp of when the SolverJob was created (server-side). Used by the UI
   * to show "running for X" and the absolute start time. Null while the job is in
   * the PREPARING placeholder state (before the server response arrives).
   */
  createdAt?: string | null;
};

export type GeneratorOptions = {
  keepExistingPositions: boolean;
  includeRoomResources: boolean;
  includeSubjectContainer: boolean;
  /** Timefold solve-time budget preset; the FE sends the key, the backend maps it to seconds. */
  speed: SolverSpeed;
};

export type WizardStep = 1 | 2;

export class SolverStore {
  public solverJob: SolverJobSnapshot | null = null;
  public jobRunning = false;
  /** True while a manual accept-current-solution request is in flight (until the job finalizes). */
  public accepting = false;
  public solverWizardOpen = false;
  public infoModalOpen = false;
  public infoModalMessage = '';
  public selectedEngine: SolverEngine = SolverEngine.Timefold;

  /** What the next run should do; set by the toolbar action before opening the wizard. */
  public task: SolverTask = 'GENERATE';
  /** OPTIMIZE only: allow the solver to also move cards of non-selected classes. */
  public allowMoveOtherClasses = false;

  /**
   * Solver engines the current user may actually pick, derived from the organization's
   * `enabledSolverEngines` (now applied to omni-admins too). Defaults to Timefold-only, which
   * is also the effective fallback when an organization has nothing configured.
   */
  public availableEngines: SolverEngine[] = [SolverEngine.Timefold];

  /** Wizard step (1 = Vortest, 2 = Optionen). The running solver lives in the inline banner. */
  public wizardStep: WizardStep = 1;
  /** Cached preflight result so step 1 doesn't re-fetch on back-navigation. */
  public preflightResult: GenericWarning[] | null = null;
  public preflightLoading = false;
  public preflightError: string | null = null;

  protected intervalId: number | undefined;

  public generatorOptions: GeneratorOptions = {
    keepExistingPositions: true,
    includeRoomResources: false,
    includeSubjectContainer: false,
    speed: 'balanced',
  };

  private onJobCompleted: (() => Promise<void>) | null = null;

  /**
   * Ziel der gespeicherten Engine-/Geschwindigkeits-Wahl. Wird hereingereicht
   * statt importiert; ohne sie bleiben die Werte im Speicher.
   */
  private userConfig: UserConfigStore | null = null;

  constructor(userConfig: UserConfigStore | null = null) {
    this.userConfig = userConfig;

    const stored = userConfig?.getSettings('solver') ?? null;
    const hasStored = userConfig?.hasStoredSettings('solver') ?? false;

    // Solange in der UserConfig nichts steht, gilt der alte localStorage-Wert —
    // er wird beim Lesen entfernt und unten einmalig uebernommen.
    const legacyEngine = readLegacyEngine();
    const legacySpeed = readLegacySpeed();

    this.selectedEngine =
      (hasStored ? null : legacyEngine) ?? (stored?.engine as SolverEngine | undefined) ?? SolverEngine.Timefold;
    this.generatorOptions.speed = (hasStored ? null : legacySpeed) ?? stored?.speed ?? 'balanced';

    if (!hasStored && (legacyEngine || legacySpeed)) {
      userConfig?.updateSettings('solver', { engine: this.selectedEngine, speed: this.generatorOptions.speed });
    }

    makeAutoObservable(this, {
      generate: action,
      abortJob: action,
      acceptCurrentSolution: action,
      clearPollingInterval: action,
      observeJob: action,
      resumeActiveJobForTimetableVersion: action,
      setSolverWizardOpen: action,
      setInfoModalOpen: action,
      setTask: action,
      setAllowMoveOtherClasses: action,
      setKeepExistingPositions: action,
      setIncludeRoomResources: action,
      setIncludeSubjectContainer: action,
      setSpeed: action,
      setSelectedEngine: action,
      setAvailableEngines: action,
      setWizardStep: action,
      nextWizardStep: action,
      prevWizardStep: action,
      setPreflightResult: action,
      setPreflightLoading: action,
      setPreflightError: action,
      closeWizardKeepJob: action,
    });
  }

  setSelectedEngine(engine: SolverEngine) {
    this.selectedEngine = engine;
    this.userConfig?.updateSettings('solver', { engine });
  }

  setAvailableEngines(engines: SolverEngine[]) {
    // Never end up with an empty set; Timefold is the universal fallback.
    this.availableEngines = engines.length > 0 ? engines : [SolverEngine.Timefold];
    // Clamp the active selection into the allowed set (e.g. a stored preference for an
    // engine the organization isn't licensed for). Done in-memory only so the user's
    // persisted preference survives a switch back to an org that allows it again.
    if (!this.availableEngines.includes(this.selectedEngine)) {
      this.selectedEngine = this.availableEngines[0];
    }
  }

  /** Parsed previewCards envelope (placements + unplaced count), computed once per change. */
  private get parsedPreview(): { placements: Map<string, PreviewPlacement>; unplacedCount: number } {
    const raw = this.solverJob?.previewCards;
    const placements = new Map<string, PreviewPlacement>();
    if (!raw) {
      return { placements, unplacedCount: 0 };
    }
    try {
      const parsed = JSON.parse(raw) as {
        unplacedCount?: number;
        cards?: Array<{
          cardUuid?: string;
          weekday?: string | null;
          startTimeGridEntryUuid?: string | null;
          endTimeGridEntryUuid?: string | null;
          roomUuids?: string[];
        }>;
      };
      for (const card of parsed.cards ?? []) {
        if (!card.cardUuid) {
          continue;
        }
        placements.set(card.cardUuid, {
          weekday: card.weekday ?? null,
          startTimeGridEntryUuid: card.startTimeGridEntryUuid ?? null,
          endTimeGridEntryUuid: card.endTimeGridEntryUuid ?? null,
          roomUuids: card.roomUuids ?? [],
        });
      }
      return {
        placements,
        unplacedCount: typeof parsed.unplacedCount === 'number' ? parsed.unplacedCount : 0,
      };
    } catch {
      // Malformed preview envelope — show no ghost cards rather than crash.
      return { placements, unplacedCount: 0 };
    }
  }

  /** Card placements proposed by the running solver, keyed by timetable card uuid. */
  get previewPlacements(): Map<string, PreviewPlacement> {
    return this.parsedPreview.placements;
  }

  /** Number of cards the solver has not placed yet in its current best solution. */
  get previewUnplacedCount(): number {
    return this.parsedPreview.unplacedCount;
  }

  /** Preview is always on; it "activates" (and takes over the grid) once the solver has placed cards. */
  get previewActive(): boolean {
    return this.previewPlacements.size > 0;
  }

  /**
   * Generator options the running job was actually started with, parsed from the
   * server-side jobOptions. Falls back to the local wizard selection while the job is
   * still preparing (before the server response carries jobOptions).
   */
  get solveOptions(): GeneratorOptions {
    const raw = this.solverJob?.jobOptions;
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<GeneratorOptions>;
        return {
          keepExistingPositions: Boolean(parsed.keepExistingPositions),
          includeRoomResources: Boolean(parsed.includeRoomResources),
          includeSubjectContainer: Boolean(parsed.includeSubjectContainer),
          speed: parsed.speed ?? this.generatorOptions.speed,
        };
      } catch {
        // Malformed jobOptions — fall back to the local selection.
      }
    }
    return this.generatorOptions;
  }

  setOnJobCompleted(callback: () => Promise<void>) {
    this.onJobCompleted = callback;
  }

  setSolverWizardOpen(open: boolean) {
    if (!open) {
      this.solverJob = null;
      this.jobRunning = false;
      this.accepting = false;
      this.wizardStep = 1;
      this.preflightResult = null;
      this.preflightLoading = false;
      this.preflightError = null;
      // Reset to the default task so the next plain "Generieren" isn't stuck on OPTIMIZE/ASSIGN_ROOMS.
      this.task = 'GENERATE';
      this.allowMoveOtherClasses = false;
    }
    this.solverWizardOpen = open;
  }

  /** Hide the wizard but keep the solver job + polling alive (background mode). */
  closeWizardKeepJob() {
    this.solverWizardOpen = false;
  }

  setWizardStep(step: WizardStep) {
    this.wizardStep = step;
  }

  nextWizardStep() {
    if (this.wizardStep < 2) {
      this.wizardStep = (this.wizardStep + 1) as WizardStep;
    }
  }

  prevWizardStep() {
    if (this.wizardStep > 1) {
      this.wizardStep = (this.wizardStep - 1) as WizardStep;
    }
  }

  setPreflightResult(result: GenericWarning[] | null) {
    this.preflightResult = result;
  }

  setPreflightLoading(loading: boolean) {
    this.preflightLoading = loading;
  }

  setPreflightError(message: string | null) {
    this.preflightError = message;
  }

  setInfoModalOpen(open: boolean, message = '') {
    this.infoModalOpen = open;
    this.infoModalMessage = message;
  }

  setTask(task: SolverTask) {
    this.task = task;
    // Bei der Raumzuordnung ist "gründlich" nicht sinnvoll – "schnell" vorwählen.
    // Nur im Speicher setzen (kein localStorage), damit die Nutzer-Voreinstellung fürs Generieren erhalten bleibt.
    if (task === 'ASSIGN_ROOMS') {
      this.generatorOptions.speed = 'fast';
    }
  }

  setAllowMoveOtherClasses(allow: boolean) {
    this.allowMoveOtherClasses = allow;
  }

  setKeepExistingPositions(keep: boolean) {
    this.generatorOptions.keepExistingPositions = keep;
  }

  setIncludeRoomResources(withRooms: boolean) {
    this.generatorOptions.includeRoomResources = withRooms;
  }

  setIncludeSubjectContainer(includeSubjectContainer: boolean) {
    this.generatorOptions.includeSubjectContainer = includeSubjectContainer;
  }

  setSpeed(speed: SolverSpeed) {
    this.generatorOptions.speed = speed;
    this.userConfig?.updateSettings('solver', { speed });
  }

  clearPollingInterval() {
    window.clearInterval(this.intervalId);
    this.intervalId = undefined;
  }

  startPolling() {
    this.clearPollingInterval();
    this.intervalId = window.setInterval(() => this.observeJob(), 3_000);
  }

  async resumeActiveJobForTimetableVersion(timetableVersionUuid: string) {
    if (!timetableVersionUuid) {
      return;
    }
    const res = await urqlClient
      .query<LatestSolverJobForVersionQuery, LatestSolverJobForVersionQueryVariables>(
        LatestSolverJobForVersionDocument,
        { versionUuid: timetableVersionUuid },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    const latest = res.data?.latestSolverJobForVersion;
    // 'EXPORTED' zählt hier mit als "nichts mehr zu tun": Offline-Solver sind abgeschafft, alte
    // Export-Jobs aus der Zeit davor sollen beim Öffnen einer Version weder eine Meldung zeigen
    // noch gepollt werden.
    if (!latest?.jobId || latest.status === 'EXPORTED' || isMathplanJobTerminalForUi(latest.status)) {
      return;
    }
    if (
      this.solverJob?.jobId === latest.jobId &&
      this.jobRunning &&
      !this.solverWizardOpen &&
      this.intervalId !== undefined
    ) {
      runInAction(() => {
        this.solverJob = {
          jobId: latest.jobId,
          status: latest.status,
          gap: latest.gap,
          solutionFound: latest.solutionFound,
          engine: latest.engine,
          scoreDetails: latest.scoreDetails,
          previewCards: latest.previewCards,
          jobOptions: latest.jobOptions,
          createdAt: latest.createdAt ?? null,
        };
      });
      return;
    }
    runInAction(() => {
      this.solverJob = {
        jobId: latest.jobId,
        status: latest.status,
        gap: latest.gap,
        solutionFound: latest.solutionFound,
        engine: latest.engine,
        scoreDetails: latest.scoreDetails,
        previewCards: latest.previewCards,
        jobOptions: latest.jobOptions,
        createdAt: latest.createdAt ?? null,
      };
      this.jobRunning = true;
      // Online solver runs resume into the inline run banner (background mode), never
      // the wizard — the wizard no longer hosts a running step.
      this.solverWizardOpen = false;
    });
    this.startPolling();
  }

  observeJob() {
    if (!this.solverJob?.jobId) {
      this.clearPollingInterval();
      return;
    }
    urqlClient
      .query<SolverJobQuery, SolverJobQueryVariables>(
        SolverJobDocument,
        { jobId: this.solverJob?.jobId ?? '' },
        { requestPolicy: 'network-only' },
      )
      .toPromise()
      .then(async (res) => {
        const current = res.data?.solverJob;
        if (!current) {
          this.clearPollingInterval();
          this.setSolverWizardOpen(false);
          return;
        }
        runInAction(() => {
          this.solverJob = {
            jobId: current.jobId,
            status: current.status,
            gap: current.gap,
            solutionFound: current.solutionFound,
            engine: current.engine,
            scoreDetails: current.scoreDetails,
            previewCards: current.previewCards,
            jobOptions: current.jobOptions,
            createdAt: current.createdAt ?? this.solverJob?.createdAt ?? null,
          };
        });

        if (isMathplanJobSuccessTerminal(current.status)) {
          this.clearPollingInterval();
          if (this.onJobCompleted) {
            await this.onJobCompleted();
          }
          this.setSolverWizardOpen(false);
          return;
        }
        if (current.status === 'ERROR') {
          this.setInfoModalOpen(true, t('pinboard.generate.error'));
          await this.abortJob();
          return;
        }
        if (isMathplanJobFailureTerminal(current.status)) {
          this.clearPollingInterval();
          this.setSolverWizardOpen(false);
        }
      })
      .catch(() => {
        this.setInfoModalOpen(true, t('pinboard.generate.error'));
        runInAction(() => {
          this.solverJob = null;
          this.jobRunning = false;
        });
        this.clearPollingInterval();
      });
  }

  async generate(timetableVersionUuid: string, classesUuids: string[], hasCardsToPlace: boolean) {
    if (!timetableVersionUuid) {
      return;
    }

    runInAction(() => {
      this.jobRunning = true;
    });

    const isGenerate = this.task === 'GENERATE';

    if (!hasCardsToPlace) {
      // Keep both keys as static t('...') calls so the i18next-parser doesn't prune them.
      this.setInfoModalOpen(true, isGenerate ? t('pinboard.generate.noCards') : t('pinboard.generate.noPlacedCards'));
      await this.abortJob();
      return;
    }

    // OPTIMIZE/ASSIGN_ROOMS are Timefold-only; GENERATE uses the chosen engine.
    const engine = isGenerate ? this.selectedEngine : SolverEngine.Timefold;

    this.solverJob = {
      status: 'PREPARING',
      gap: 0,
      jobId: '',
      solutionFound: false,
      engine,
      // Local timestamp as a fallback before the server response arrives; the next observeJob()
      // tick overwrites it with the authoritative server-side createdAt from GraphQL.
      createdAt: new Date().toISOString(),
    };

    const response = await solveTimetableVersionWithParams(timetableVersionUuid, {
      engine,
      task: this.task,
      // GENERATE: `classes` = hard scope. OPTIMIZE/ASSIGN_ROOMS: `selectedClasses` = movable set.
      classes: isGenerate ? classesUuids : [],
      selectedClasses: isGenerate ? [] : classesUuids,
      allowMoveOtherClasses: this.task === 'OPTIMIZE' ? this.allowMoveOtherClasses : false,
      keepExistingPositions: this.generatorOptions.keepExistingPositions,
      includeRoomResources: this.task === 'ASSIGN_ROOMS' ? true : this.generatorOptions.includeRoomResources,
      includeSubjectContainer: this.generatorOptions.includeSubjectContainer,
      speed: this.generatorOptions.speed,
    });

    if (!response?.data || response.error || response.data.status === 'ERROR') {
      this.failStartAndStayInWizard();
      return;
    }

    const d = response.data;
    runInAction(() => {
      this.solverJob = {
        jobId: d.jobId,
        status: d.status,
        gap: d.gap,
        solutionFound: d.solutionFound,
        engine: d.engine != null ? (d.engine as SolverEngine) : engine,
        scoreDetails: 'scoreDetails' in d ? (d as { scoreDetails?: string | null }).scoreDetails : undefined,
        // REST response doesn't carry createdAt; reuse PREPARING-placeholder timestamp,
        // first observeJob tick replaces it with the GraphQL value.
        createdAt: this.solverJob?.createdAt ?? new Date().toISOString(),
      };
    });

    this.startPolling();

    // Default to background mode: hide the wizard so the live preview takes over the board
    // while the solver runs. The SolverRunBanner keeps status + the preview toggle visible.
    this.closeWizardKeepJob();
  }

  /**
   * Solver-Start fehlgeschlagen: Wizard (Schritt 2) offen lassen und den Start-Button
   * wieder freigeben, damit der Nutzer es direkt erneut versuchen kann. Es läuft noch
   * kein Job (keine Job-ID) — es gibt also nichts zu canceln und nichts zu pollen.
   */
  private failStartAndStayInWizard() {
    this.setInfoModalOpen(true, t('pinboard.generate.error'));
    runInAction(() => {
      this.solverJob = null;
      this.jobRunning = false;
    });
  }

  /**
   * Stop the running solver and import its current best solution instead of discarding it.
   * The backend persists the best-so-far, tears the solver down and enqueues the normal
   * import; observeJob() then sees the job reach IMPORTED and runs onJobCompleted (board
   * reload + wizard close), which also clears the accepting flag.
   */
  async acceptCurrentSolution() {
    if (!this.solverJob?.jobId || this.accepting) {
      return;
    }
    runInAction(() => {
      this.accepting = true;
    });
    const res = (await finishTimetableVersionGeneration(this.solverJob.jobId)) as { error?: string | null } | undefined;
    if (res?.error) {
      runInAction(() => {
        this.accepting = false;
      });
      this.setInfoModalOpen(true, t('pinboard.generate.wizard.step3.acceptSolutionError'));
      return;
    }
    // Success: keep accepting set so the buttons stay pending until polling drives the job
    // to IMPORTED and onJobCompleted closes the wizard (which resets the flag).
  }

  async abortJob() {
    if (this.solverJob?.jobId) {
      this.clearPollingInterval();
      await cancelTimetableVersionGeneration(this.solverJob.jobId);
      this.solverJob = null;
      this.jobRunning = false;
    }
    this.setSolverWizardOpen(false);
  }
}

/** @deprecated use SolverJobSnapshot */
export type MathplanJob = SolverJobSnapshot;
